'use client'

import { KnockProvider } from '@knocklabs/react'
import React, { useCallback, useEffect, useState } from 'react'
import { useGenerateKnockTokenMutation, useIdentifyKnockUserMutation } from '~/bff/graphql/generated/graphql'
import { getFullName } from '~/utils/get-full-name'
import { useUserContext } from '../user-provider'
import { KnockProviderContextProvider } from './knock-provider-context'

export function KnockProviderWrapper({ children }: React.PropsWithChildren) {
  const [identifyKnockUser] = useIdentifyKnockUserMutation()
  const [generateKnockToken] = useGenerateKnockTokenMutation()
  const [token, setToken] = useState<string>()
  const [companyUserId, setCompanyUserId] = useState<string>()
  const { user, isSignedIn, currentWorkspace } = useUserContext()

  const generateUserToken = useCallback(
    async (companyUserId: string) => {
      const { data } = await generateKnockToken({
        variables: {
          input: {
            id: companyUserId,
          },
        },
      })

      setToken(data?.GenerateKnockToken.token ?? undefined)
    },
    [generateKnockToken],
  )

  useEffect(() => {
    setCompanyUserId(currentWorkspace?.companyUser?.id?.toString())

    if (isSignedIn && companyUserId) {
      generateUserToken(companyUserId)
      identifyKnockUser({
        variables: {
          input: {
            properties: {
              id: companyUserId,
              name: getFullName(user?.firstName, user?.lastName),
              email: user?.EmailVerification[0].emailAddress,
            },
          },
        },
      })
    }
  }, [isSignedIn, identifyKnockUser, user, generateUserToken, companyUserId, currentWorkspace?.companyUser?.id])

  const isKnockInitialized = Boolean(token && isSignedIn)

  return (
    <KnockProviderContextProvider isKnockInitialized={isKnockInitialized}>
      {isKnockInitialized ? (
        <KnockProvider
          apiKey={process.env.NEXT_PUBLIC_KNOCK_API_KEY ?? ''}
          userId={companyUserId}
          userToken={token}
          onUserTokenExpiring={async () => {
            if (companyUserId) {
              await generateUserToken(companyUserId)
            }
          }}
        >
          {children}
        </KnockProvider>
      ) : (
        children
      )}
    </KnockProviderContextProvider>
  )
}
